import {
  clearStoredTokens,
  loadStoredTokens,
  saveStoredTokens,
  shouldRefresh,
  type StoredAuthTokens,
} from './tokenStore';
import type {
  ApiKeyListResponse,
  AuthMeResponse,
  AuthTokenResponse,
  CreateApiKeyResponse,
  ApproveDeviceResponse,
  WalletBalanceResponse,
  PortalSessionResponse,
  TopUpSessionResponse,
  LedgerResponse,
} from '../types';
import type { AuthScope } from '../types';

const AUTH_BASE = '/auth';

let refreshInFlight: Promise<StoredAuthTokens> | null = null;

export class AuthHttpError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

function tokensFromResponse(
  resp: AuthTokenResponse,
  nowMs: number,
  prev: StoredAuthTokens | null,
): StoredAuthTokens {
  return {
    accessToken: resp.access_token,
    refreshToken: resp.refresh_token,
    issuedAtMs: nowMs,
    expiresInSec: resp.expires_in,
    scope: resp.scope ?? prev?.scope,
    userId: resp.user_id ?? prev?.userId,
  };
}

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function fetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  const payload = await readJsonSafe(res);
  if (!res.ok) {
    throw new AuthHttpError(`HTTP ${res.status}`, res.status, payload);
  }
  return payload as T;
}

function withAuthHeader(init: RequestInit, accessToken: string): RequestInit {
  return {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      authorization: `Bearer ${accessToken}`,
    },
  };
}

async function refreshAndPersist(prev: StoredAuthTokens): Promise<StoredAuthTokens> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const nowMs = Date.now();
        const resp = await fetchJson<AuthTokenResponse>(`${AUTH_BASE}/token`, {
          method: 'POST',
          body: JSON.stringify({
            grant_type: 'refresh_token',
            refresh_token: prev.refreshToken,
          }),
        });
        const next = tokensFromResponse(resp, nowMs, prev);
        saveStoredTokens(next);
        return next;
      } catch (err) {
        clearStoredTokens();
        throw err;
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  return refreshInFlight;
}

async function ensureFreshTokens(prev: StoredAuthTokens): Promise<StoredAuthTokens> {
  const nowMs = Date.now();
  if (!shouldRefresh(prev, nowMs)) return prev;
  return refreshAndPersist(prev);
}

async function authenticatedFetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const stored = loadStoredTokens();
  if (!stored) {
    throw new AuthHttpError('not_authenticated', 401, null);
  }

  const fresh = await ensureFreshTokens(stored);

  try {
    return await fetchJson<T>(path, withAuthHeader(init, fresh.accessToken));
  } catch (err) {
    if (!(err instanceof AuthHttpError) || err.status !== 401) throw err;
    const next = await refreshAndPersist(fresh);
    return await fetchJson<T>(path, withAuthHeader(init, next.accessToken));
  }
}

export async function signup(params: {
  name: string;
  email: string;
  password: string;
}): Promise<StoredAuthTokens> {
  const nowMs = Date.now();
  const resp = await fetchJson<AuthTokenResponse>(`${AUTH_BASE}/signup`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  const tokens = tokensFromResponse(resp, nowMs, null);
  saveStoredTokens(tokens);
  return tokens;
}

export async function login(params: { email: string; password: string }): Promise<StoredAuthTokens> {
  const nowMs = Date.now();
  const resp = await fetchJson<AuthTokenResponse>(`${AUTH_BASE}/login`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  const tokens = tokensFromResponse(resp, nowMs, null);
  saveStoredTokens(tokens);
  return tokens;
}

export async function logout(): Promise<void> {
  const stored = loadStoredTokens();
  clearStoredTokens();
  if (!stored) return;
  await fetchJson<{ status: 'ok' }>(`${AUTH_BASE}/logout`, {
    method: 'POST',
    body: JSON.stringify({ refresh_token: stored.refreshToken }),
  });
}

export async function getMe(): Promise<AuthMeResponse> {
  return authenticatedFetchJson<AuthMeResponse>(`${AUTH_BASE}/me`, { method: 'GET' });
}

export async function getWalletBalance(): Promise<WalletBalanceResponse> {
  return authenticatedFetchJson<WalletBalanceResponse>(`${AUTH_BASE}/wallet/balance`, { method: 'GET' });
}

export async function listApiKeys(): Promise<ApiKeyListResponse> {
  return authenticatedFetchJson<ApiKeyListResponse>(`${AUTH_BASE}/api-keys`, { method: 'GET' });
}

export async function createApiKey(): Promise<CreateApiKeyResponse> {
  return authenticatedFetchJson<CreateApiKeyResponse>(`${AUTH_BASE}/api-keys`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function revokeApiKeyById(id: number): Promise<void> {
  await authenticatedFetchJson<{ status: 'ok' }>(`${AUTH_BASE}/api-keys/revoke`, {
    method: 'POST',
    body: JSON.stringify({ id }),
  });
}

export async function approveDeviceCode(userCode: string, scope: AuthScope[] = ['api']): Promise<ApproveDeviceResponse> {
  return authenticatedFetchJson<ApproveDeviceResponse>(`${AUTH_BASE}/device/approve`, {
    method: 'POST',
    body: JSON.stringify({ user_code: userCode, scope }),
  });
}

export async function getPortalSession(returnUrl?: string): Promise<PortalSessionResponse> {
  return authenticatedFetchJson<PortalSessionResponse>(`${AUTH_BASE}/billing/portal-session`, {
    method: 'POST',
    body: JSON.stringify({ return_url: returnUrl }),
  });
}

export async function getTopUpSession(returnUrl: string, credits: number): Promise<TopUpSessionResponse> {
  return authenticatedFetchJson<TopUpSessionResponse>(`${AUTH_BASE}/billing/top-up-session`, {
    method: 'POST',
    body: JSON.stringify({ return_url: returnUrl, credits }),
  });
}

export async function getLedger(limit = 20): Promise<LedgerResponse> {
  return authenticatedFetchJson<LedgerResponse>(`${AUTH_BASE}/wallet/ledger?limit=${limit}`, {
    method: 'GET',
  });
}
